import {
  HttpEvent,
  HttpEventCallbacks,
  httpDelete,
  httpGet,
  httpPost,
  httpPut,
} from "@/events/eventsDomain";
import { toQueryString } from "@/utils/queryParams";
import type {
  TemplateDTO,
  TemplateOverviewDTO,
  TemplateType,
  TemplateVisibilityRequest,
  SaveTemplateRequest,
  SaveTemplateResponse,
  TemplateContent,
  UpdateTemplateRequest,
} from "@/templates/templateDomain";

export type TemplateApiClient = {
  getTemplates: (
    accountId: string,
    type: TemplateType,
    params: HttpEventCallbacks<TemplateOverviewDTO>,
  ) => HttpEvent<TemplateOverviewDTO>;
  getCompanyTemplates: (
    companyId: string,
    type: TemplateType,
    params: HttpEventCallbacks<TemplateDTO[]>,
  ) => HttpEvent<TemplateDTO[]>;
  getContent: (
    templateId: string,
    companyId: string | undefined,
    params: HttpEventCallbacks<TemplateContent>,
  ) => HttpEvent<TemplateContent>;
  save: (
    request: SaveTemplateRequest,
    params: HttpEventCallbacks<SaveTemplateResponse>,
  ) => HttpEvent<SaveTemplateResponse>;
  update: (
    templateId: string,
    request: UpdateTemplateRequest,
    params: HttpEventCallbacks<TemplateOverviewDTO>,
  ) => HttpEvent<TemplateOverviewDTO>;
  delete: (
    templateId: string,
    params: HttpEventCallbacks<TemplateOverviewDTO>,
  ) => HttpEvent<TemplateOverviewDTO>;
  updateVisibility: (
    templateId: string,
    request: TemplateVisibilityRequest,
    params: HttpEventCallbacks<TemplateOverviewDTO>,
  ) => HttpEvent<TemplateOverviewDTO>;
};

export const templateApiClient: TemplateApiClient = {
  getTemplates: (accountId, type, params) =>
    httpGet({ url: `/templates${toQueryString({ accountId, type })}`, ...params }),
  getCompanyTemplates: (companyId, type, params) =>
    httpGet({
      url: `/templates/company/${encodeURIComponent(companyId)}${toQueryString({ type })}`,
      ...params,
    }),
  getContent: (templateId, companyId, params) =>
    httpGet({
      url: `/templates/${encodeURIComponent(templateId)}/content${toQueryString({ companyId })}`,
      ...params,
    }),
  save: (request, params) => httpPost({ url: "/templates", data: request, ...params }),
  update: (templateId, request, params) =>
    httpPut({ url: `/templates/${encodeURIComponent(templateId)}`, data: request, ...params }),
  delete: (templateId, params) =>
    httpDelete({ url: `/templates/${encodeURIComponent(templateId)}`, ...params }),
  updateVisibility: (templateId, request, params) =>
    httpPut({
      url: `/templates/${encodeURIComponent(templateId)}/visibility`,
      data: request,
      ...params,
    }),
};
